import Box, { type BoxProps } from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'
import { useRef, useState, type DragEvent, type ReactNode } from 'react'

type Props = {
  onFile: (file: File) => void
  children: ReactNode
  /** Shown over the children while a file is dragged above them. */
  overlayText?: string
  sx?: BoxProps['sx']
}

/**
 * True for a file dragged from the computer. Also stops the event: the upload dialog renders
 * in a portal, yet React still bubbles its events to the panel's zone underneath.
 */
const takeFiles = (e: DragEvent) => {
  if (!e.dataTransfer.types.includes('Files')) return false
  e.stopPropagation()
  return true
}

/** Accepts a file dragged from the computer. Only the first one is taken when several are dropped. */
export function FileDropZone({ onFile, children, overlayText = 'שחררו כאן כדי להעלות', sx }: Props) {
  const [active, setActive] = useState(false)
  // dragenter/dragleave fire for every child crossed, so count them rather than toggle.
  const depth = useRef(0)

  return (
    <Box
      sx={{ position: 'relative', ...sx }}
      onDragEnter={(e) => {
        if (!takeFiles(e)) return
        e.preventDefault()
        depth.current += 1
        setActive(true)
      }}
      onDragOver={(e) => {
        if (!takeFiles(e)) return
        // Without this the browser opens the file instead of dropping it here.
        e.preventDefault()
        e.dataTransfer.dropEffect = 'copy'
      }}
      onDragLeave={(e) => {
        if (!takeFiles(e)) return
        depth.current = Math.max(0, depth.current - 1)
        if (depth.current === 0) setActive(false)
      }}
      onDrop={(e) => {
        if (!takeFiles(e)) return
        e.preventDefault()
        depth.current = 0
        setActive(false)
        const file = e.dataTransfer.files[0]
        if (file) onFile(file)
      }}
    >
      {children}
      {active ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 2,
            borderStyle: 'dashed',
            borderColor: 'primary.main',
            borderRadius: 1,
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.85),
            pointerEvents: 'none',
          }}
        >
          <Typography variant="h6" color="primary">
            {overlayText}
          </Typography>
        </Box>
      ) : null}
    </Box>
  )
}
